import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Trash2,
  Type,
  Bold,
  Italic,
  Strikethrough,
  Upload,
  X,
  AlertCircle,
  Loader,
  CheckCircle,
  Send,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Wand2,
} from "lucide-react";
import WhatsAppPreview from "../components/WhatsAppNewPreview";
import {
  createTemplate,
  submitTemplateForApproval,
  generateTemplateFromAI,
} from "../apis/template";
import { useTemplateState } from "../hooks/useTemplateState";
import { useTemplateVariables } from "../hooks/useTemplateVariables";
import { validateTemplate, validateTemplateName } from "../utils/templateValidation";
import { getWabaAccount, buildTemplatePayload } from "../utils/templateUtils";
import { transformDuplicateTemplate, transformAiGeneratedTemplate } from "../utils/templateDataTransformers";
import { applyFormatting, insertVariableAtPosition } from "../utils/templateFormatting";

const CreateTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Use custom hooks for state management
  const {
    templateData,
    setTemplateData,
    getComponent,
    updateComponent,
    removeComponent,
    getHeaderFormat,
    getHeaderText,
    getBody,
    getFooter,
    getButtons,
  } = useTemplateState();

  // Use custom hook for variable management
  const {
    addVariable: addVariableToText,
    updateVariableSample,
    removeVariable,
    handleTextChange,
    isUpdatingBodyRef,
    isUpdatingHeaderRef,
  } = useTemplateVariables(templateData, setTemplateData, getComponent, updateComponent);

  // UI state
  const [errors, setErrors] = useState({});
  const [textareaRef, setTextareaRef] = useState(null);
  const [bodyCursorPosition, setBodyCursorPosition] = useState(0);
  const [headerCursorPosition, setHeaderCursorPosition] = useState(0);
  const [headerTextareaRef, setHeaderTextareaRef] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [createdTemplateId, setCreatedTemplateId] = useState(null);

  // AI Template Generation States
  const [aiPrompt, setAiPrompt] = useState("");
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(true);
  const [aiError, setAiError] = useState("");

  // Handle duplicate template data from navigation
  useEffect(() => {
    if (location.state?.duplicateTemplate) {
      const duplicateData = location.state.duplicateTemplate;
      const transformedData = transformDuplicateTemplate(duplicateData);
      
      setTemplateData((prev) => ({
        ...prev,
        ...transformedData,
      }));

      // Clear the state to prevent re-applying on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state, setTemplateData]);

  const handleInputChange = (field, value) => {
    if (["name", "category", "language", "wabaId", "phoneId"].includes(field)) {
      setTemplateData((prev) => ({ ...prev, [field]: value }));
    } else if (field === "body") {
      updateComponent("BODY", { text: value });
    } else if (field === "footer") {
      if (value.trim()) {
        updateComponent("FOOTER", { text: value });
      } else {
        removeComponent("FOOTER");
      }
    } else if (field === "headerFormat") {
      if (value === "NONE") {
        removeComponent("HEADER");
      } else {
        const existingHeader = getComponent("HEADER");
        updateComponent("HEADER", {
          type: "HEADER",
          format: value,
          text: existingHeader?.text || "",
        });
      }
    } else if (field === "headerText") {
      updateComponent("HEADER", { text: value });
    }

    // Real-time validation for template name
    if (field === "name") {
      const nameError = validateTemplateName(value);
      setErrors((prev) => ({ ...prev, name: nameError || "" }));
    } else if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateData((prev) => ({
          ...prev,
          headerFile: file,
          headerFilePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setTemplateData((prev) => ({
      ...prev,
      headerFile: null,
      headerFilePreview: null,
    }));
  };

  const addVariable = (target = "body") => {
    const existingVariables = templateData.variables.filter((v) => v.target === target);
    const cursorPos = target === "body" ? bodyCursorPosition : headerCursorPosition;

    // Check limits
    if (target === "header" && existingVariables.length >= 1) {
      setErrors((prev) => ({
        ...prev,
        headerText: "Only 1 variable allowed in header",
      }));
      setTimeout(() => setErrors((prev) => ({ ...prev, headerText: "" })), 3000);
      return;
    }

    if (target === "body" && existingVariables.length >= 10) {
      setErrors((prev) => ({
        ...prev,
        body: "Maximum 10 variables allowed in body",
      }));
      setTimeout(() => setErrors((prev) => ({ ...prev, body: "" })), 3000);
      return;
    }

    const result = addVariableToText(target, cursorPos);
    if (result.error) {
      setErrors((prev) => ({ ...prev, [target === "body" ? "body" : "headerText"]: result.error }));
      setTimeout(() => setErrors((prev) => ({ ...prev, [target === "body" ? "body" : "headerText"]: "" })), 3000);
      return;
    }

    // Update cursor position
    setTimeout(() => {
      const textarea = target === "body" ? textareaRef : headerTextareaRef;
      if (textarea && result.newCursorPosition !== undefined) {
        textarea.setSelectionRange(result.newCursorPosition, result.newCursorPosition);
        textarea.focus();
      }
    }, 0);
  };

  const insertFormatting = (format, target = "body") => {
    const textarea = target === "body" ? textareaRef : headerTextareaRef;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = target === "body" ? getBody() : getHeaderText();
    const selectedText = currentText.substring(start, end);

    const formattedText = applyFormatting(currentText, selectedText, format);
    const symbolLength = format === "bold" || format === "italic" || format === "strikethrough" ? 1 : 0;

    const newText =
      currentText.substring(0, start) +
      formattedText +
      currentText.substring(end);

    updateComponent(target === "body" ? "BODY" : "HEADER", { text: newText });

    setTimeout(() => {
      if (selectedText) {
        textarea.setSelectionRange(start + symbolLength, end + symbolLength);
      } else {
        textarea.setSelectionRange(start + symbolLength, start + symbolLength);
      }
      textarea.focus();
    }, 0);
  };

  const handleBodyChange = (e) => {
    const newText = e.target.value;
    const cursorPos = e.target.selectionStart;
    setBodyCursorPosition(cursorPos);
    
    if (errors.body) {
      setErrors((prev) => ({ ...prev, body: "" }));
    }
    
    handleTextChange(newText, "body", cursorPos);
  };

  const handleHeaderTextChange = (e) => {
    const newText = e.target.value;
    const cursorPos = e.target.selectionStart;
    setHeaderCursorPosition(cursorPos);
    
    if (errors.headerText) {
      setErrors((prev) => ({ ...prev, headerText: "" }));
    }
    
    handleTextChange(newText, "header", cursorPos);
  };

  const addButton = (type = "QUICK_REPLY") => {
    const buttons = getButtons();
    const totalButtons = buttons.length;
    const quickReplyCount = buttons.filter(
      (b) => b.type === "QUICK_REPLY"
    ).length;
    const urlCount = buttons.filter((b) => b.type === "URL").length;
    const callCount = buttons.filter((b) => b.type === "PHONE_NUMBER").length;

    if (totalButtons >= 6) return;
    if (type === "QUICK_REPLY" && quickReplyCount >= 3) return;
    if (type === "URL" && urlCount >= 2) return;
    if (type === "PHONE_NUMBER" && callCount >= 1) return;

    const newButton = {
      id: Date.now(),
      type,
      text: "",
      url: type === "URL" ? "" : undefined,
      phone_number: type === "PHONE_NUMBER" ? "" : undefined,
      example: type === "URL" ? [] : undefined,
      urlVariable: type === "URL" ? null : undefined,
    };

    const buttonsComponent = getComponent("BUTTONS");
    if (buttonsComponent) {
      updateComponent("BUTTONS", {
        buttons: [...buttons, newButton],
      });
    } else {
      updateComponent("BUTTONS", {
        type: "BUTTONS",
        buttons: [newButton],
      });
    }
  };

  const updateButton = (id, field, value) => {
    const buttons = getButtons();
    const updatedButtons = buttons.map((btn) => {
      if (String(btn.id) === String(id)) {
        const updatedBtn = { ...btn, [field]: value };
        
        if (field === "url" && btn.type === "URL") {
          const urlVariables = (value || "").match(/\{\{(\d+)\}\}/g) || [];
          
          if (urlVariables.length > 1) {
            setErrors((prev) => ({
              ...prev,
              [`url_${id}`]: "Only one variable allowed in URL",
            }));
            setTimeout(() => {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[`url_${id}`];
                return newErrors;
              });
            }, 3000);
            return btn;
          }
          
          if (urlVariables.length === 1) {
            const varMatch = urlVariables[0].match(/\{\{(\d+)\}\}/);
            if (varMatch) {
              updatedBtn.urlVariable = {
                name: varMatch[1],
                sample: updatedBtn.urlVariable?.sample || "",
              };
            }
          } else {
            delete updatedBtn.urlVariable;
          }
        }
        
        return updatedBtn;
      }
      return btn;
    });
    updateComponent("BUTTONS", { buttons: updatedButtons });
  };

  const updateUrlVariableSample = (buttonId, sampleValue) => {
    const buttons = getButtons();
    const updatedButtons = buttons.map((btn) => {
      if (btn.id === buttonId && btn.type === "URL" && btn.urlVariable) {
        return {
          ...btn,
          urlVariable: {
            ...btn.urlVariable,
            sample: sampleValue,
          },
        };
      }
      return btn;
    });
    updateComponent("BUTTONS", { buttons: updatedButtons });
  };

  const removeButton = (id) => {
    const buttons = getButtons();
    const filteredButtons = buttons.filter((btn) => btn.id !== id);
    
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`url_${id}`];
      return newErrors;
    });
    
    if (filteredButtons.length === 0) {
      removeComponent("BUTTONS");
    } else {
      updateComponent("BUTTONS", { buttons: filteredButtons });
    }
  };

  const validateAndSubmit = () => {
    const wabaAccount = getWabaAccount();
    const newErrors = validateTemplate(templateData, wabaAccount);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAiError("Please enter a prompt describing your template");
      return;
    }

    setIsAiGenerating(true);
    setAiError("");

    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await generateTemplateFromAI(authToken, {
        prompt: aiPrompt,
        category: templateData.category,
        language: templateData.language,
      });

      if (response.data.success) {
        const generated = response.data.template;
        const transformed = transformAiGeneratedTemplate(generated);

        setTemplateData((prev) => ({
          ...prev,
          name: generated.name || prev.name,
          category: generated.category || prev.category,
          language: generated.language || prev.language,
          components: transformed.components,
          variables: transformed.variables,
        }));

        setAiPrompt("");
        setAiExpanded(false);
        setSuccessMessage("✨ Template generated successfully! Review and customize as needed.");
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      console.error("AI Generation Error:", err);
      setAiError(err.response?.data?.error || "Failed to generate template. Please try again.");
    } finally {
      setIsAiGenerating(false);
    }
  };

  const handleCreateAndSubmit = async () => {
    if (!validateAndSubmit()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const wabaAccount = getWabaAccount();
      const templatePayload = buildTemplatePayload(templateData, wabaAccount);

      const formData = new FormData();
      formData.append("templateData", JSON.stringify(templatePayload));
      formData.append("wabaId", templatePayload.waba_id);
      formData.append("phoneId", templatePayload.phone_id);

      const headerFormat = getHeaderFormat();
      if (
        templateData.headerFile &&
        !templateData.header_media_url &&
        ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat)
      ) {
        formData.append("headerFile", templateData.headerFile);
      }
      
      if (templateData.header_media_url) {
        formData.append("header_media_url", templateData.header_media_url);
      }

      const createResponse = await createTemplate(authToken, formData);
      if (createResponse.data.success) {
        const templateId = createResponse.data.metaTemplateId;
        setCreatedTemplateId(templateId);
        navigate("/templates");
      }
    } catch (err) {
      console.error("Error creating/submitting template:", err);
      let errorMessage = "Failed to create/submit template";
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
        if (err.response.data.details) {
          errorMessage += `: ${err.response.data.details}`;
        }
      }
      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitExistingTemplate = async () => {
    if (!createdTemplateId) return;

    setIsLoading(true);
    setErrors({});
    try {
      const authToken = localStorage.getItem("userAccessToken");
      const wabaAccount = getWabaAccount();
      const phoneId = wabaAccount?.phoneId || templateData.phoneId;

      const response = await submitTemplateForApproval(
        authToken,
        createdTemplateId,
        phoneId
      );

      if (response.data.success) {
        setSuccessMessage(
          `✅ Template submitted to Meta! Status: ${response.data.status}`
        );

        setTimeout(() => {
          navigate("/templates");
        }, 2000);
      }
    } catch (err) {
      console.error("Error submitting template:", err);
      setErrors({
        submit:
          err.response?.data?.error || "Failed to submit template to Meta",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const previewData = {
    headerFormat: getHeaderFormat(),
    headerText: getHeaderText(),
    headerFilePreview: templateData.headerFilePreview || templateData.header_media_url,
    header_media_url: templateData.header_media_url || templateData.headerFilePreview,
    headerFile: templateData.headerFile,
    body: getBody(),
    footer: getFooter(),
    buttons: getButtons(),
    variables: templateData.variables,
  };

  return (
    <div className="min-h-screen bg-[#fff] pt-4">
      {/* Top Bar - Fixed with margin */}
      <div className="bg-white border-b border-gray-200 sticky top-14 md:top-16 mb-4">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-4 lg:px-5 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/templates")}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-700" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  Create New Template
                </h1>
                <p className="text-xs text-gray-600">
                  Design your WhatsApp message template
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCreateAndSubmit}
                disabled={isLoading}
                className="px-4 py-2.5 text-sm text-gray-900 font-semibold rounded-xl transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 border border-gray-300"
                style={{ backgroundColor: "#eefa94" }}
              >
                {isLoading ? (
                  <Loader size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                Create & Submit
              </button>

              {createdTemplateId && !isLoading && (
                <button
                  onClick={handleSubmitExistingTemplate}
                  className="px-4 py-2.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
                >
                  <Send size={16} />
                  Submit to Meta
                </button>
              )}
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3">
              <CheckCircle className="text-emerald-600" size={18} />
              <p className="text-emerald-700 text-sm font-medium">
                {successMessage}
              </p>
            </div>
          )}

          {/* Error Message */}
          {errors.submit && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0" size={18} />
              <div>
                <p className="text-red-700 text-sm font-medium">
                  {errors.submit}
                </p>
                <p className="text-red-600 text-xs mt-1">
                  Please check your template and try again.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-4 lg:px-5 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Template Builder - Scrollable */}
          <div className="lg:col-span-2">
            <div className="space-y-4 lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto lg:overflow-x-hidden lg:pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {errors.wabaId && (
                <div className="bg-red-50 rounded-2xl border border-red-200 p-4">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <AlertCircle size={16} />
                    {errors.wabaId}
                  </p>
                </div>
              )}

              {/* AI Template Generator Card */}
              <div className="rounded-2xl border border-gray-300 overflow-hidden shadow-sm" style={{ backgroundColor: "#eefa94" }}>
                <button
                  onClick={() => setAiExpanded(!aiExpanded)}
                  className="w-full px-5 py-4 flex items-center justify-between hover:opacity-90 transition-opacity"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-gray-900 rounded-xl shadow-md">
                      <Sparkles size={20} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        AI Template Generator
                        <span className="text-xs font-semibold px-2.5 py-0.5 bg-gray-900 text-white rounded-full">
                          Beta
                        </span>
                      </h2>
                      <p className="text-xs text-gray-800">
                        Describe your template and let AI create it for you
                      </p>
                    </div>
                  </div>
                  {aiExpanded ? (
                    <ChevronUp size={20} className="text-gray-900" />
                  ) : (
                    <ChevronDown size={20} className="text-gray-900" />
                  )}
                </button>

                {aiExpanded && (
                  <div className="px-5 pb-5 space-y-4">
                    <div className="relative">
                      <textarea
                        value={aiPrompt}
                        onChange={(e) => {
                          setAiPrompt(e.target.value);
                          if (aiError) setAiError("");
                        }}
                        placeholder="Example: Create a marketing template for a Diwali sale with 30% discount. Include a greeting, offer details, and a Shop Now button linking to our website..."
                        rows={4}
                        className={`w-full px-4 py-3 text-sm border ${
                          aiError ? "border-red-300" : "border-gray-300"
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent resize-none bg-white placeholder:text-gray-500`}
                      />
                      <div className="absolute bottom-3 right-3 text-xs text-gray-600">
                        {aiPrompt.length}/500
                      </div>
                    </div>

                    {aiError && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                        <AlertCircle
                          size={16}
                          className="text-red-500 flex-shrink-0"
                        />
                        <p className="text-sm text-red-600">{aiError}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-800">
                        💡 Tip: Be specific about buttons, variables, and tone
                      </p>
                      <button
                        onClick={handleAiGenerate}
                        disabled={isAiGenerating || !aiPrompt.trim()}
                        className="px-4 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg"
                      >
                        {isAiGenerating ? (
                          <>
                            <Loader size={16} className="animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Wand2 size={16} />
                            Generate Template
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Basic Info Card */}
              <div
                className="rounded-2xl border border-gray-200 p-4"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <h2 className="text-sm font-bold text-gray-900 mb-3">
                  Basic Information
                </h2>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={templateData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value.toLowerCase())
                      }
                      placeholder="e.g., welcome_message"
                      className={`w-full px-4 py-2.5 text-sm border ${
                        errors.name ? "border-red-300" : "border-gray-300"
                      } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white`}
                    />
                    {errors.name && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {errors.name}
                      </p>
                    )}
                    <p className="text-xs text-gray-600 mt-1">
                      Use lowercase, numbers, and underscores only (no spaces)
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Category *
                      </label>
                      <select
                        value={templateData.category}
                        onChange={(e) =>
                          handleInputChange("category", e.target.value)
                        }
                        className="w-full px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                      >
                        <option value="MARKETING">Marketing</option>
                        <option value="UTILITY">Utility</option>
                        <option value="AUTHENTICATION">Authentication</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Language *
                      </label>
                      <select
                        value={templateData.language}
                        onChange={(e) =>
                          handleInputChange("language", e.target.value)
                        }
                        className="w-full px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                      >
                        <option value="en_US">English (US)</option>
                        <option value="en_GB">English (UK)</option>
                        <option value="hi_IN">Hindi</option>
                        <option value="es_ES">Spanish</option>
                        <option value="fr_FR">French</option>
                        <option value="pt_BR">Portuguese (BR)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Header Card */}
              <div
                className="rounded-2xl border border-gray-200 p-4"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <h2 className="text-sm font-bold text-gray-900 mb-3">
                  Header
                </h2>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Template Type *
                    </label>
                    <select
                      value={getHeaderFormat()}
                      onChange={(e) => {
                        const newFormat = e.target.value;
                        handleInputChange("headerFormat", newFormat);
                        if (newFormat === "TEXT" || newFormat === "LOCATION") {
                          setTemplateData((prev) => ({
                            ...prev,
                            headerFile: null,
                            headerFilePreview: null,
                          }));
                        }
                      }}
                      className="w-full px-4 py-2.5 text-sm font-medium border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                    >
                      <option value="NONE">None</option>
                      <option value="TEXT">Text</option>
                      <option value="IMAGE">Image</option>
                      <option value="VIDEO">Video</option>
                      <option value="DOCUMENT">Document</option>
                    </select>
                    <p className="text-xs text-gray-600 mt-1">
                      Select the type of media for your template header
                    </p>
                  </div>

                  {getHeaderFormat() === "TEXT" && (
                    <div>
                      <div className="flex items-center justify-between mb-2.5">
                        <label className="block text-xs font-bold text-gray-700">
                          Header Text *
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-600">
                            ({templateData.variables.filter((v) => v.target === "header").length}/1 variable)
                          </span>
                          <button
                            onClick={() => addVariable("header")}
                            disabled={
                              templateData.variables.filter((v) => v.target === "header").length >= 1
                            }
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Type size={12} />
                            Add Variable
                          </button>
                        </div>
                      </div>
                      <textarea
                        ref={(el) => setHeaderTextareaRef(el)}
                        value={getHeaderText()}
                        onChange={handleHeaderTextChange}
                        onSelect={(e) =>
                          setHeaderCursorPosition(e.target.selectionStart)
                        }
                        placeholder="Enter header text..."
                        rows={2}
                        maxLength={60}
                        className={`w-full px-4 py-2.5 text-sm border ${
                          errors.headerText
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none bg-white`}
                      />
                      {errors.headerText && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {errors.headerText}
                        </p>
                      )}
                      <p className="text-xs text-gray-600 mt-1">
                        {getHeaderText().length}/60 characters
                      </p>

                      {/* Header Variable Samples Section */}
                      {templateData.variables.filter((v) => v.target === "header")
                        .length > 0 && (
                        <div className="mt-3 p-3 bg-white rounded-xl border border-gray-300">
                          <h3 className="text-xs font-bold text-gray-700 mb-2.5">
                            Header Variable Sample
                          </h3>
                          <div className="space-y-2">
                            {templateData.variables
                              .filter((v) => v.target === "header")
                              .sort((a, b) => {
                                const numA = parseInt(a.name) || 0;
                                const numB = parseInt(b.name) || 0;
                                return numA - numB;
                              })
                              .map((variable) => (
                                <div
                                  key={variable.id}
                                  className="flex items-center gap-2"
                                >
                                  <span className="text-xs font-semibold text-gray-700 min-w-[50px]">
                                    {`{{${variable.name}}}`} =
                                  </span>
                                  <input
                                    type="text"
                                    value={variable.sample}
                                    onChange={(e) =>
                                      updateVariableSample(
                                        variable.id,
                                        e.target.value
                                      )
                                    }
                                    placeholder="Enter sample value"
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                                  />
                                  <button
                                    onClick={() => removeVariable(variable.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remove variable"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {["IMAGE", "VIDEO", "DOCUMENT"].includes(
                    getHeaderFormat()
                  ) && (
                    <div>
                      {templateData.header_media_url && !templateData.headerFile && (
                        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                            <div className="flex-1">
                              <p className="text-xs font-bold text-blue-900 mb-1">
                                Media URL Available
                              </p>
                              <p className="text-xs text-blue-700 mb-2">
                                Using existing media URL. No upload required. You can upload a new file to replace it.
                              </p>
                              <a
                                href={templateData.header_media_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 underline break-all"
                              >
                                {templateData.header_media_url}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!templateData.headerFilePreview && !templateData.header_media_url ? (
                        <div>
                          <label className="block">
                            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors cursor-pointer bg-white">
                              <Upload
                                className="mx-auto mb-2 text-gray-400"
                                size={36}
                              />
                              <p className="text-sm font-bold text-gray-700 mb-1">
                                Click to upload{" "}
                                {getHeaderFormat().toLowerCase()}
                              </p>
                              <p className="text-xs text-gray-600">
                                {getHeaderFormat() === "IMAGE" &&
                                  "PNG, JPG up to 5MB"}
                                {getHeaderFormat() === "DOCUMENT" &&
                                  "PDF, DOC up to 100MB"}
                                {getHeaderFormat() === "VIDEO" &&
                                  "MP4, AVI up to 16MB"}
                              </p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept={
                                getHeaderFormat() === "IMAGE"
                                  ? "image/*"
                                  : getHeaderFormat() === "DOCUMENT"
                                  ? ".pdf,.doc,.docx"
                                  : "video/*"
                              }
                              onChange={handleFileUpload}
                            />
                          </label>
                        </div>
                      ) : (
                        <div className="relative">
                          {getHeaderFormat() === "IMAGE" && (
                            <div className="relative rounded-xl overflow-hidden border w-fit border-gray-200 bg-white p-2">
                              <img
                                src={templateData.headerFilePreview || templateData.header_media_url}
                                alt="Preview"
                                className="max-w-[300px] max-h-48 w-auto h-auto object-contain rounded-lg"
                              />
                              {templateData.headerFile && (
                                <button
                                  onClick={removeFile}
                                  className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md"
                                >
                                  <X size={16} />
                                </button>
                              )}
                              {templateData.header_media_url && !templateData.headerFile && (
                                <div className="absolute top-3 right-3 px-2.5 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full shadow-md">
                                  Existing URL
                                </div>
                              )}
                            </div>
                          )}

                          {getHeaderFormat() === "DOCUMENT" && (
                            <div className="p-4 bg-white rounded-xl border border-gray-200 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-3 bg-red-100 rounded-xl">
                                  <svg
                                    className="w-6 h-6 text-red-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <p className="font-bold text-gray-900">
                                    {templateData.headerFile?.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {(
                                      templateData.headerFile?.size / 1024
                                    ).toFixed(2)}{" "}
                                    KB
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={removeFile}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <X size={20} />
                              </button>
                            </div>
                          )}

                          {getHeaderFormat() === "VIDEO" && (
                            <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-white p-2">
                              <video
                                src={templateData.headerFilePreview || templateData.header_media_url}
                                className="max-w-[300px] max-h-48 w-auto h-auto object-contain rounded-lg"
                                controls
                              />
                              {templateData.headerFile && (
                                <button
                                  onClick={removeFile}
                                  className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-md"
                                >
                                  <X size={16} />
                                </button>
                              )}
                              {templateData.header_media_url && !templateData.headerFile && (
                                <div className="absolute top-3 right-3 px-2.5 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full shadow-md">
                                  Existing URL
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {errors.headerFile && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {errors.headerFile}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Body Card */}
              <div
                className="rounded-2xl border border-gray-200 p-4"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-bold text-gray-900">
                    Message Body *
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 font-medium">
                      ({templateData.variables.filter((v) => v.target === "body").length}/10 variables)
                    </span>
                    <button
                      onClick={() => addVariable("body")}
                      disabled={
                        templateData.variables.filter((v) => v.target === "body").length >= 10
                      }
                      className="flex items-center gap-1.5 px-3 py-2 bg-gray-900 text-white text-xs font-semibold rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Type size={14} />
                      Add Variable
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 p-2 bg-white rounded-xl border border-gray-300">
                    <button
                      onClick={() => insertFormatting("bold", "body")}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Bold"
                    >
                      <Bold size={16} className="text-gray-700" />
                    </button>
                    <button
                      onClick={() => insertFormatting("italic", "body")}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Italic"
                    >
                      <Italic size={16} className="text-gray-700" />
                    </button>
                    <button
                      onClick={() => insertFormatting("strikethrough", "body")}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Strikethrough"
                    >
                      <Strikethrough size={16} className="text-gray-700" />
                    </button>
                  </div>

                  <div>
                    <textarea
                      ref={(el) => setTextareaRef(el)}
                      value={getBody()}
                      onChange={handleBodyChange}
                      onSelect={(e) =>
                        setBodyCursorPosition(e.target.selectionStart)
                      }
                      placeholder="Enter your message here. Click 'Add Variable' to insert {{1}}, {{2}}, etc..."
                      rows={6}
                      maxLength={1024}
                      className={`w-full px-4 py-2.5 text-sm border ${
                        errors.body ? "border-red-300" : "border-gray-300"
                      } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent resize-none font-mono bg-white`}
                    />
                    {errors.body && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {errors.body}
                      </p>
                    )}
                    <div className="flex justify-between items-center mt-2">
                      <p className="text-xs text-gray-600">
                        *bold* _italic_ ~strikethrough~
                      </p>
                      <p
                        className={`text-xs font-medium ${
                          getBody().length > 1024
                            ? "text-red-600"
                            : "text-gray-600"
                        }`}
                      >
                        {getBody().length}/1024
                      </p>
                    </div>
                  </div>

                  {/* Variable Samples Section */}
                  {templateData.variables.filter((v) => v.target === "body")
                    .length > 0 && (
                    <div className="mt-4 p-3 bg-white rounded-xl border border-gray-300">
                      <h3 className="text-xs font-bold text-gray-700 mb-2.5">
                        Variable Samples
                      </h3>
                      <div className="space-y-2">
                        {templateData.variables
                          .filter((v) => v.target === "body")
                          .sort((a, b) => {
                            const numA = parseInt(a.name) || 0;
                            const numB = parseInt(b.name) || 0;
                            return numA - numB;
                          })
                          .map((variable) => (
                            <div
                              key={variable.id}
                              className="flex items-center gap-2"
                            >
                              <span className="text-xs font-semibold text-gray-700 min-w-[50px]">
                                {`{{${variable.name}}}`} =
                              </span>
                              <input
                                type="text"
                                value={variable.sample}
                                onChange={(e) =>
                                  updateVariableSample(
                                    variable.id,
                                    e.target.value
                                  )
                                }
                                placeholder="Enter sample value"
                                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                              />
                              <button
                                onClick={() => removeVariable(variable.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove variable"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Card */}
              <div
                className="rounded-2xl border border-gray-200 p-4"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <h2 className="text-sm font-bold text-gray-900 mb-3">
                  Footer (Optional)
                </h2>
                <input
                  type="text"
                  value={getFooter()}
                  onChange={(e) => handleInputChange("footer", e.target.value)}
                  placeholder="e.g., Thank you for choosing us!"
                  maxLength={60}
                  className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent bg-white"
                />
                <p className="text-xs text-gray-600 mt-1">
                  {getFooter().length}/60 characters
                </p>
              </div>

              {/* Buttons Card */}
              <div
                className="rounded-2xl border border-gray-200 p-4"
                style={{ backgroundColor: "#e9eeea" }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-bold text-gray-900">Buttons</h2>
                    <span className="text-xs text-gray-600 font-medium">
                      ({getButtons().length}/6)
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {(() => {
                      const buttons = getButtons();
                      const totalButtons = buttons.length;
                      const quickReplyCount = buttons.filter(
                        (b) => b.type === "QUICK_REPLY"
                      ).length;
                      const urlCount = buttons.filter(
                        (b) => b.type === "URL"
                      ).length;
                      const callCount = buttons.filter(
                        (b) => b.type === "PHONE_NUMBER"
                      ).length;

                      const isQuickReplyDisabled =
                        totalButtons >= 6 || quickReplyCount >= 3;
                      const isUrlDisabled = totalButtons >= 6 || urlCount >= 2;
                      const isCallDisabled =
                        totalButtons >= 6 || callCount >= 1;

                      return (
                        <>
                          <button
                            onClick={() => addButton("QUICK_REPLY")}
                            disabled={isQuickReplyDisabled}
                            className="px-3 py-1.5 text-xs bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-semibold rounded-lg transition-colors border border-gray-300"
                            title={
                              isQuickReplyDisabled
                                ? `Quick Reply limit reached (${quickReplyCount}/3)`
                                : `Add Quick Reply (${quickReplyCount}/3)`
                            }
                          >
                            + Quick ({quickReplyCount}/3)
                          </button>
                          <button
                            onClick={() => addButton("URL")}
                            disabled={isUrlDisabled}
                            className="px-3 py-1.5 text-xs bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-semibold rounded-lg transition-colors border border-gray-300"
                            title={
                              isUrlDisabled
                                ? `URL limit reached (${urlCount}/2)`
                                : `Add URL (${urlCount}/2)`
                            }
                          >
                            + URL ({urlCount}/2)
                          </button>
                          <button
                            onClick={() => addButton("PHONE_NUMBER")}
                            disabled={isCallDisabled}
                            className="px-3 py-1.5 text-xs bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 font-semibold rounded-lg transition-colors border border-gray-300"
                            title={
                              isCallDisabled
                                ? `Call limit reached (${callCount}/1)`
                                : `Add Call (${callCount}/1)`
                            }
                          >
                            + Call ({callCount}/1)
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {getButtons().length > 0 ? (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    {getButtons().map((button, index) => {
                      const getTypeBadgeColor = (type) => {
                        if (type === "QUICK_REPLY")
                          return "bg-blue-100 text-blue-700 border-blue-200";
                        if (type === "URL")
                          return "bg-purple-100 text-purple-700 border-purple-200";
                        if (type === "PHONE_NUMBER")
                          return "bg-green-100 text-green-700 border-green-200";
                        return "bg-gray-100 text-gray-700 border-gray-200";
                      };

                      return (
                        <div
                          key={button.id}
                          className="p-3 border border-gray-300 rounded-xl bg-white hover:shadow-sm transition-all"
                        >
                          <div className="flex items-center justify-between mb-2.5">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-xs font-bold px-2 py-1 rounded-lg border ${getTypeBadgeColor(
                                  button.type
                                )}`}
                              >
                                {button.type === "QUICK_REPLY"
                                  ? "Quick"
                                  : button.type === "PHONE_NUMBER"
                                  ? "Call"
                                  : "URL"}
                              </span>
                              <span className="text-xs text-gray-600 font-medium">
                                #{index + 1}
                              </span>
                            </div>
                            <button
                              onClick={() => removeButton(button.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove button"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>

                          <div className="space-y-2">
                            <input
                              type="text"
                              value={button.text}
                              onChange={(e) =>
                                updateButton(button.id, "text", e.target.value)
                              }
                              placeholder="Button text"
                              maxLength={20}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                            />

                            {button.type === "URL" && (
                              <>
                                <input
                                  type="url"
                                  value={button.url || ""}
                                  onChange={(e) =>
                                    updateButton(
                                      button.id,
                                      "url",
                                      e.target.value
                                    )
                                  }
                                  placeholder="https://example.com/track/{{1}}"
                                  className={`w-full px-3 py-2 text-sm border ${
                                    errors[`url_${button.id}`]
                                      ? "border-red-300"
                                      : "border-gray-300"
                                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent`}
                                />
                                {errors[`url_${button.id}`] && (
                                  <p className="text-xs text-red-600 flex items-center gap-1">
                                    <AlertCircle size={12} />
                                    {errors[`url_${button.id}`]}
                                  </p>
                                )}
                                {button.url &&
                                  button.urlVariable &&
                                  button.urlVariable.name && (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-semibold text-gray-700 min-w-[60px]">
                                          {`{{${button.urlVariable.name}}}`} =
                                        </span>
                                        <input
                                          type="text"
                                          value={button.urlVariable.sample || ""}
                                          onChange={(e) =>
                                            updateUrlVariableSample(
                                              button.id,
                                              e.target.value
                                            )
                                          }
                                          placeholder="Enter sample value (e.g., ORDER_12345)"
                                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                                        />
                                      </div>
                                      <p className="text-xs text-gray-500">
                                        Sample value for URL variable preview
                                      </p>
                                    </div>
                                  )}
                              </>
                            )}

                            {button.type === "PHONE_NUMBER" && (
                              <input
                                type="tel"
                                value={button.phone_number || ""}
                                onChange={(e) =>
                                  updateButton(
                                    button.id,
                                    "phone_number",
                                    e.target.value
                                  )
                                }
                                placeholder="+1 555 123 4567"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 text-sm border-2 border-dashed border-gray-300 rounded-xl bg-white">
                    No buttons added yet
                  </div>
                )}

                <p className="text-xs text-gray-600 mt-3 font-medium">
                  Quick Reply (max 3) • URL (max 2, 1 variable max) • Call (max 1) • Total:{" "}
                  {getButtons().length}/6
                </p>
              </div>
            </div>
          </div>

          {/* Right: Fixed Preview */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-32 lg:overflow-visible lg:z-10">
              <WhatsAppPreview templateData={previewData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplate;
